import { AdFarmConfig } from "./AdFarmConfig";
import { AdSource, MobileTask, RewardContext, RewardSessionStatus } from "./AdFarmTypes";
import { AdFarmSession } from "./AdFarmSession";

type RewardAdResult = {
    rewarded: boolean;
    sourceKey?: AdSource;
    message?: string;
};

type NativeRewardAds = {
    showRewardedAd(options: {
        provider: "gromore" | "taku";
        placementId: string;
        transId: string;
        customData: string;
        sourceKey: AdSource;
    }): Promise<RewardAdResult>;
};

export class RewardAdBridge {
    private nativeBridge(): NativeRewardAds | null {
        const host = globalThis as unknown as { AdFarmNativeBridge?: { rewardAds?: NativeRewardAds } };
        return host.AdFarmNativeBridge?.rewardAds || null;
    }

    isConfigured(): boolean {
        return AdFarmConfig.ADS_PROVIDER !== "mock" && !!AdFarmConfig.placementId && !!this.nativeBridge();
    }

    isMockMode(): boolean {
        return AdFarmConfig.ADS_PROVIDER === "mock";
    }

    /**
     * Rewarded-ad gate used by gameplay helpers. In the local Android build we
     * deliberately simulate a completed ad so the whole unlock flow can be
     * tested without creating fake withdrawable income. Production builds use
     * the configured native rewarded-ad bridge and server verification.
     */
    async showUnlock(task: MobileTask, onStatus?: (message: string) => void): Promise<boolean> {
        if (this.isMockMode()) {
            onStatus?.("激励广告加载中…");
            await this.delay(650);
            onStatus?.("广告播放中，请完整观看 2 秒");
            await this.delay(1000);
            onStatus?.("广告播放中，请完整观看 1 秒");
            await this.delay(1000);
            onStatus?.("广告观看完成，正在解锁");
            await this.delay(350);
            return true;
        }

        const status = await this.show(task, onStatus);
        return !!status.verified || status.sessionStatus === "settled";
    }

    async show(task: MobileTask, onStatus?: (message: string) => void): Promise<RewardSessionStatus> {
        const session = AdFarmSession.instance;
        if (!session.authenticated || !session.overview) throw new Error("请先登录后再领取红包");
        const bridge = this.nativeBridge();
        const placementId = AdFarmConfig.placementId;
        if (AdFarmConfig.ADS_PROVIDER === "mock") throw new Error("当前为 Mock 模式，不能生成正式红包收益");
        if (!placementId || !bridge) throw new Error("激励广告尚未配置，请先接入原生广告 SDK 和广告位 ID");

        const taskId = task.taskId || task.id || "";
        const sourceKey = task.sourceKey;
        if (!taskId || !sourceKey) throw new Error("后台任务配置不完整，请联系管理员");
        const account = session.revenue.accountFor(session.overview, session.role) || {};
        const userId = account.id || account.inviteCode || account.phone || "mobile-user";
        const context = await session.rewards.prepare({ role: session.role, taskId, sourceKey, userId, placementId });
        let cancelled = false;

        try {
            onStatus?.("广告加载中…");
            await session.rewards.report(context, "load", "start");
            const result = await bridge.showRewardedAd({
                provider: AdFarmConfig.ADS_PROVIDER as "gromore" | "taku",
                placementId,
                transId: context.transId,
                customData: session.rewards.buildRewardExtra(context),
                sourceKey,
            });
            await session.rewards.report(context, "close", result.rewarded ? "rewarded" : "not_rewarded", result.message || "");
            if (!result.rewarded) {
                await session.rewards.cancel(context, result.message || "用户未完整观看广告");
                cancelled = true;
                throw new Error(result.message || "完整观看广告后才能领取奖励");
            }
            await session.rewards.markRewarded(context);
            onStatus?.("奖励确认中…");
            const status = await session.rewards.waitForSettlement(context);
            if (status.verified || status.sessionStatus === "settled") await session.refreshOverview();
            return status;
        } catch (error) {
            await session.rewards.report(context, "failure", "client_error", error instanceof Error ? error.message : "unknown");
            if (!cancelled) {
                try {
                    await session.rewards.cancel(context, error instanceof Error ? error.message : "广告播放失败");
                } catch (_cancelError) {
                    // 保留原始广告错误；取消失败可在服务端诊断页继续排查。
                }
            }
            throw error;
        }
    }

    private delay(milliseconds: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, milliseconds));
    }
}
